import { useState, useRef, useCallback, useMemo } from 'react';
import { View, Text, TextInput, Pressable, Platform, useWindowDimensions } from 'react-native';
import Animated, { FadeInUp, FadeInDown, FadeIn } from 'react-native-reanimated';
import { TurnstileWidget } from '@/components/submission/TurnstileWidget';
import { handleError, reportError } from '@/lib/errors';
import { supabase } from '@/lib/supabase';
import { track } from '@/lib/analytics';
import { Copy, Check, Linkedin, Twitter, MessageCircle, ChevronLeft, AlertTriangle } from 'lucide-react-native';

const TURNSTILE_SITE_KEY = process.env.EXPO_PUBLIC_TURNSTILE_SITE_KEY || '1x00000000000000000000AA';

type FormState = 'idle' | 'submitting' | 'success' | 'error';
type Step = 'role' | 'email' | 'check-position';
type UserRole = 'startup' | 'investor';

interface WaitlistResult {
  position: number;
  effectivePosition?: number;
  referralCode: string;
  totalCount?: number;
  referralCount?: number;
  pendingVerification?: boolean;
}

interface WaitlistFormProps {
  colorScheme?: 'dark' | 'light';
}

const FORM_COLORS = {
  dark: {
    textPrimary: '#FFFFFF',
    textSecondary: 'rgba(255,255,255,0.6)',
    textTertiary: 'rgba(255,255,255,0.5)',
    textMuted: 'rgba(255,255,255,0.45)',
    textFaint: 'rgba(255,255,255,0.4)',
    textPlaceholder: 'rgba(255,255,255,0.3)',
    cardBg: 'rgba(255,255,255,0.05)',
    cardBgHover: 'rgba(255,255,255,0.08)',
    inputBg: 'rgba(255,255,255,0.06)',
    border: 'rgba(255,255,255,0.12)',
    borderLight: 'rgba(255,255,255,0.1)',
    borderInput: 'rgba(255,255,255,0.15)',
    dotInactive: 'rgba(255,255,255,0.15)',
    ctaBg: '#0a1628',
    captchaTheme: 'dark' as const,
  },
  light: {
    textPrimary: '#1d1d1f',
    textSecondary: '#48484a',
    textTertiary: '#636366',
    textMuted: '#8e8e93',
    textFaint: '#aeaeb2',
    textPlaceholder: '#c7c7cc',
    cardBg: 'rgba(0,0,0,0.04)',
    cardBgHover: 'rgba(0,0,0,0.06)',
    inputBg: '#ffffff',
    border: 'rgba(0,0,0,0.12)',
    borderLight: 'rgba(0,0,0,0.08)',
    borderInput: 'rgba(0,0,0,0.15)',
    dotInactive: 'rgba(0,0,0,0.12)',
    ctaBg: '#ffffff',
    captchaTheme: 'light' as const,
  },
};

export function WaitlistForm({ colorScheme = 'dark' }: WaitlistFormProps) {
  const fc = FORM_COLORS[colorScheme];
  const [step, setStep] = useState<Step>('role');
  const [role, setRole] = useState<UserRole | null>(null);
  const [fullName, setFullName] = useState('');
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [title, setTitle] = useState('');
  const [email, setEmail] = useState('');
  const [formState, setFormState] = useState<FormState>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [result, setResult] = useState<WaitlistResult | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const turnstileRef = useRef<string | null>(null);
  const { width } = useWindowDimensions();
  const isMobile = width < 768;

  const accent = role === 'investor' ? '#818cf8' : '#4fd1c5';
  const accentDim = role === 'investor' ? 'rgba(129,140,248,0.15)' : 'rgba(79,209,197,0.15)';

  // Detect referral code from URL
  const hasReferral = useMemo(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return false;
    const params = new URLSearchParams(window.location.search);
    return !!params.get('ref');
  }, []);

  // Personal email domains — soft warning, not a block
  const PERSONAL_DOMAINS = useMemo(() => new Set([
    'gmail.com', 'googlemail.com', 'yahoo.com', 'yahoo.fr', 'hotmail.com',
    'hotmail.fr', 'outlook.com', 'live.com', 'aol.com', 'icloud.com',
    'me.com', 'mac.com', 'protonmail.com', 'proton.me', 'mail.com',
    'gmx.com', 'gmx.net', 'yandex.com', 'zoho.com',
  ]), []);

  const isPersonalEmail = useMemo(() => {
    if (role === 'investor') return false; // Angels often use personal email
    const domain = email.trim().toLowerCase().split('@')[1];
    return domain ? PERSONAL_DOMAINS.has(domain) : false;
  }, [email, role, PERSONAL_DOMAINS]);

  const getRefParam = (): string | undefined => {
    if (Platform.OS !== 'web' || typeof window === 'undefined') return undefined;
    const params = new URLSearchParams(window.location.search);
    return params.get('ref') || undefined;
  };

  const handleSelectRole = (r: UserRole) => {
    setRole(r);
    setStep('email');
  };

  const [checkEmail, setCheckEmail] = useState('');
  const [checkState, setCheckState] = useState<'idle' | 'loading' | 'error'>('idle');
  const [checkError, setCheckError] = useState('');

  const handleBack = () => {
    if (step === 'email') { setStep('role'); }
    else if (step === 'check-position') { setStep('role'); setCheckEmail(''); setCheckState('idle'); setCheckError(''); }
  };

  const handleCheckPosition = useCallback(async () => {
    if (!checkEmail.trim()) return;
    setCheckState('loading');
    setCheckError('');

    try {
      const { data, error } = await supabase.functions.invoke('waitlist-position', {
        body: { email: checkEmail.trim().toLowerCase() },
      });

      if (error || !data) {
        const status = (error as any)?.context?.status ?? 0;
        setCheckError(status === 404 ? 'Email not found on the waitlist.' : 'Something went wrong. Try again.');
        setCheckState('error');
        return;
      }

      setResult({
        position: data.originalPosition,
        effectivePosition: data.effectivePosition,
        referralCode: data.referralCode,
        totalCount: data.totalCount,
        referralCount: data.referralCount,
      });
      setFormState('success');
    } catch (err) {
      const { message } = handleError(err, { context: 'WaitlistForm.handleCheckPosition' });
      setCheckError(message);
      setCheckState('error');
    }
  }, [checkEmail]);

  const handleSubmit = useCallback(async () => {
    if (!email.trim() || !fullName.trim() || !captchaToken) return;

    setFormState('submitting');
    setErrorMessage('');

    try {
      const ref = getRefParam();

      const { data, error } = await supabase.functions.invoke('waitlist-signup', {
        body: {
          email: email.trim().toLowerCase(),
          captchaToken,
          ref,
          role: role || undefined,
          fullName: fullName.trim(),
          linkedinUrl: linkedinUrl.trim() || undefined,
          companyName: companyName.trim() || undefined,
          title: title.trim() || undefined,
        },
      });

      if (error) {
        const status = (error as any).context?.status ?? 0;
        let body: any = {};
        try {
          body = await (error as any).context?.json?.();
        } catch (e) {
          // Response body may already be consumed — non-fatal, diagnostics only.
          reportError(e, { context: 'WaitlistForm.parseErrorBody', level: 'warning', silent: true });
        }
        const msg = body?.error || error.message || '';

        if (status === 409 || msg.includes('already on the waitlist')) {
          // Fetch existing position instead of showing an error
          try {
            const { data: posData } = await supabase.functions.invoke('waitlist-position', {
              body: { email: email.trim().toLowerCase() },
            });
            if (posData) {
              setResult({
                position: posData.originalPosition,
                effectivePosition: posData.effectivePosition,
                referralCode: posData.referralCode,
                totalCount: posData.totalCount,
                referralCount: posData.referralCount,
              });
              setFormState('success');
              return;
            }
          } catch (e) {
            // Fall through to the friendly message below if position lookup fails.
            reportError(e, { context: 'WaitlistForm.positionLookup', level: 'warning', silent: true });
          }
          setErrorMessage('This email is already on the waitlist.');
        } else if (status === 429) {
          setErrorMessage('Too many attempts. Please try again later.');
        } else if (status === 403) {
          setErrorMessage('CAPTCHA verification failed. Please try again.');
        } else {
          setErrorMessage('Something went wrong. Please try again.');
        }
        setFormState('error');
        return;
      }

      setResult(data as WaitlistResult);
      setFormState('success');
      track('waitlist_signup', {
        position: (data as any).effectivePosition,
        referred: !!ref,
        role: role || 'undecided',
      });
    } catch (err) {
      const { message, isCancelled } = handleError(err, { context: 'WaitlistForm.handleSubmit' });
      if (!isCancelled) {
        setErrorMessage(message);
        setFormState('error');
      }
    }
  }, [email, captchaToken, role, fullName, linkedinUrl, companyName, title]);

  const referralLink = result ? `https://inverio.ai?ref=${result.referralCode}` : '';

  const copyReferralLink = useCallback(async () => {
    if (Platform.OS !== 'web' || !referralLink) return;
    try {
      await navigator.clipboard.writeText(referralLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = referralLink;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [referralLink]);

  const shareOnLinkedIn = () => {
    if (Platform.OS !== 'web') return;
    const url = encodeURIComponent(referralLink);
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${url}`, '_blank');
  };

  const shareOnTwitter = () => {
    if (Platform.OS !== 'web') return;
    const text = encodeURIComponent('Just joined the @InverioAI waitlist — AI-powered investment readiness scoring. Join me and skip the line!');
    const url = encodeURIComponent(referralLink);
    window.open(`https://twitter.com/intent/tweet?text=${text}&url=${url}`, '_blank');
  };

  const shareOnWhatsApp = () => {
    if (Platform.OS !== 'web') return;
    const text = encodeURIComponent(`Check out Inverio — AI-powered investment readiness scoring for startups and investors. Join the waitlist: ${referralLink}`);
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  // ── Step indicator ──
  const StepDots = () => {
    const steps: Step[] = ['role', 'email'];
    const currentIdx = steps.indexOf(step);
    return (
      <View style={{ flexDirection: 'row', gap: 8, justifyContent: 'center', marginBottom: 24 }}>
        {steps.map((s, i) => (
          <View
            key={s}
            style={{
              width: i <= currentIdx ? 24 : 8,
              height: 8,
              borderRadius: 4,
              backgroundColor: i <= currentIdx ? accent : fc.dotInactive,
            }}
          />
        ))}
      </View>
    );
  };

  // ── Success state ──
  const isReturning = formState === 'success' && result && (result.referralCount ?? -1) >= 0 && !result.pendingVerification;
  const isPendingVerification = formState === 'success' && result?.pendingVerification;

  if (isPendingVerification && result) {
    return (
      <Animated.View entering={FadeIn.duration(500)}>
        <View style={{ alignItems: 'center', gap: 16 }}>
          <Text style={{ color: accent, fontSize: 28, fontWeight: '700' }}>
            Check your email
          </Text>
          <Text style={{ color: fc.textPrimary, fontSize: 16, textAlign: 'center', maxWidth: 400, lineHeight: 24 }}>
            We sent a verification link to{'\n'}
            <Text style={{ fontWeight: '700' }}>{email.trim().toLowerCase()}</Text>
          </Text>
          <View style={{
            backgroundColor: fc.cardBg,
            borderRadius: 12,
            paddingHorizontal: 20,
            paddingVertical: 16,
            borderWidth: 1,
            borderColor: fc.borderLight,
            maxWidth: 420,
            width: '100%',
          }}>
            <Text style={{ color: fc.textSecondary, fontSize: 14, textAlign: 'center', lineHeight: 22 }}>
              Click the link in the email to confirm your spot on the waitlist. Check your spam folder if you don't see it.
            </Text>
          </View>
          <Text style={{ color: fc.textMuted, fontSize: 12, textAlign: 'center', maxWidth: 360 }}>
            Your position (#{result.position}) is reserved and will be confirmed once you verify.
          </Text>
        </View>
      </Animated.View>
    );
  }

  if (formState === 'success' && result) {
    return (
      <Animated.View entering={FadeIn.duration(500)}>
        <View style={{ alignItems: 'center', gap: 16 }}>
          <Text style={{ color: accent, fontSize: 28, fontWeight: '700' }}>
            {isReturning ? 'Welcome back!' : "You're on the list!"}
          </Text>
          <Text style={{ color: fc.textPrimary, fontSize: 18, fontWeight: '600' }}>
            You're #{result.effectivePosition} in line.
          </Text>

          {/* Referral stats — shown for returning users */}
          {isReturning && (result.referralCount ?? 0) > 0 && (
            <View style={{
              flexDirection: 'row',
              gap: 16,
              maxWidth: 480,
              width: '100%',
              justifyContent: 'center',
            }}>
              <View style={{
                flex: 1,
                backgroundColor: fc.cardBg,
                borderRadius: 12,
                paddingVertical: 14,
                alignItems: 'center',
                borderWidth: 1,
                borderColor: fc.borderLight,
              }}>
                <Text style={{ color: accent, fontSize: 24, fontWeight: '700' }}>
                  {result.referralCount}
                </Text>
                <Text style={{ color: fc.textTertiary, fontSize: 12, marginTop: 2 }}>
                  {result.referralCount === 1 ? 'Referral' : 'Referrals'}
                </Text>
              </View>
              <View style={{
                flex: 1,
                backgroundColor: fc.cardBg,
                borderRadius: 12,
                paddingVertical: 14,
                alignItems: 'center',
                borderWidth: 1,
                borderColor: fc.borderLight,
              }}>
                <Text style={{ color: accent, fontSize: 24, fontWeight: '700' }}>
                  +{(result.referralCount ?? 0) * 5}
                </Text>
                <Text style={{ color: fc.textTertiary, fontSize: 12, marginTop: 2 }}>
                  Spots gained
                </Text>
              </View>
            </View>
          )}

          <View style={{
            backgroundColor: fc.cardBg,
            borderRadius: 12,
            paddingHorizontal: 16,
            paddingVertical: 12,
            borderWidth: 1,
            borderColor: fc.borderLight,
            maxWidth: 480,
            width: '100%',
            gap: 6,
          }}>
            <Text style={{ color: accent, fontSize: 15, fontWeight: '700', textAlign: 'center' }}>
              Every referral moves you 5 spots up
            </Text>
            <Text style={{ color: fc.textTertiary, fontSize: 12, textAlign: 'center' }}>
              {isReturning
                ? 'Keep sharing your referral link to climb higher.'
                : 'Invites go out in order — the higher you are, the sooner you get yours.'}
            </Text>
          </View>

          {/* Referral link */}
          <View style={{
            backgroundColor: fc.cardBg,
            borderRadius: 12,
            padding: 16,
            width: '100%',
            maxWidth: 480,
            borderWidth: 1,
            borderColor: fc.borderLight,
          }}>
            <Text style={{ color: fc.textSecondary, fontSize: 13, marginBottom: 8 }}>
              Share with 3 friends to skip ahead:
            </Text>
            <Pressable
              onPress={copyReferralLink}
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                backgroundColor: fc.cardBgHover,
                borderRadius: 8,
                padding: 12,
                gap: 8,
              }}
            >
              <Text style={{ color: fc.textPrimary, fontSize: 14, flex: 1 }} numberOfLines={1}>
                {referralLink}
              </Text>
              {copied ? (
                <Check size={18} color={accent} />
              ) : (
                <Copy size={18} color={fc.textSecondary} />
              )}
            </Pressable>
            {copied && (
              <Text style={{ color: accent, fontSize: 12, marginTop: 4 }}>
                Copied!
              </Text>
            )}
          </View>

          {/* Social share buttons */}
          <View style={{ flexDirection: 'row', gap: 12, marginTop: 8 }}>
            {[
              { onPress: shareOnLinkedIn, Icon: Linkedin },
              { onPress: shareOnTwitter, Icon: Twitter },
              { onPress: shareOnWhatsApp, Icon: MessageCircle },
            ].map(({ onPress, Icon }, i) => (
              <Pressable
                key={i}
                onPress={onPress}
                style={{
                  backgroundColor: fc.cardBgHover,
                  borderRadius: 10,
                  padding: 12,
                  borderWidth: 1,
                  borderColor: fc.borderLight,
                }}
              >
                <Icon size={20} color={fc.textPrimary} />
              </Pressable>
            ))}
          </View>
        </View>
      </Animated.View>
    );
  }

  // ── Check position step ──
  if (step === 'check-position') {
    return (
      <Animated.View entering={FadeIn.duration(400)} style={{ alignItems: 'center', gap: 14, width: '100%' }}>
        <Text style={{ color: fc.textPrimary, fontSize: 18, fontWeight: '700' }}>
          Check your position
        </Text>
        <Text style={{ color: fc.textSecondary, fontSize: 14 }}>
          Enter the email you signed up with
        </Text>
        <View style={{ flexDirection: isMobile ? 'column' : 'row', maxWidth: 480, width: '100%', gap: 10 }}>
          <TextInput
            value={checkEmail}
            onChangeText={setCheckEmail}
            placeholder="your@email.com"
            placeholderTextColor={fc.textPlaceholder}
            keyboardType="email-address"
            autoCapitalize="none"
            autoCorrect={false}
            editable={checkState !== 'loading'}
            style={{
              flex: 1,
              backgroundColor: fc.inputBg,
              borderWidth: 1,
              borderColor: fc.borderInput,
              borderRadius: 10,
              paddingHorizontal: 16,
              paddingVertical: 12,
              color: fc.textPrimary,
              fontSize: 14,
            }}
          />
          <Pressable
            onPress={handleCheckPosition}
            disabled={!checkEmail.trim() || checkState === 'loading'}
            style={{
              backgroundColor: (!checkEmail.trim() || checkState === 'loading') ? 'rgba(79,209,197,0.4)' : '#4fd1c5',
              borderRadius: 10,
              paddingHorizontal: 24,
              paddingVertical: 12,
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Text style={{ color: fc.ctaBg, fontWeight: '700', fontSize: 15 }}>
              {checkState === 'loading' ? 'Checking...' : 'Check'}
            </Text>
          </Pressable>
        </View>
        {checkState === 'error' && checkError && (
          <Animated.View entering={FadeInUp.duration(300)}>
            <Text style={{ color: '#f87171', fontSize: 14 }}>{checkError}</Text>
          </Animated.View>
        )}
        <Pressable onPress={handleBack} style={{ marginTop: 4 }}>
          <Text style={{ color: fc.textFaint, fontSize: 13 }}>
            ← Back to sign up
          </Text>
        </Pressable>
      </Animated.View>
    );
  }

  // ── Step 1: Role selection ──
  if (step === 'role') {
    return (
      <Animated.View entering={FadeIn.duration(400)} style={{ alignItems: 'center', width: '100%' }}>
        <StepDots />
        {hasReferral && (
          <Animated.View entering={FadeIn.duration(400)} style={{
            backgroundColor: 'rgba(79,209,197,0.12)',
            borderRadius: 10,
            paddingHorizontal: 16,
            paddingVertical: 10,
            marginBottom: 12,
            borderWidth: 1,
            borderColor: 'rgba(79,209,197,0.25)',
            maxWidth: 480,
            width: '100%',
          }}>
            <Text style={{ color: '#4fd1c5', fontSize: 13, fontWeight: '600', textAlign: 'center' }}>
              You were referred by a friend — sign up and you'll both move up the list!
            </Text>
          </Animated.View>
        )}
        <Text style={{ color: fc.textSecondary, fontSize: 14, marginBottom: 16 }}>
          I am a...
        </Text>
        <View style={{ flexDirection: isMobile ? 'column' : 'row', gap: 16, maxWidth: 480, width: '100%' }}>
          {([
            { r: 'startup' as UserRole, label: 'Startup', sub: 'Looking to raise or get scored', color: '#4fd1c5' },
            { r: 'investor' as UserRole, label: 'Investor', sub: 'Scouting or building pipeline', color: '#818cf8' },
          ]).map(({ r, label, sub, color }) => (
            <Pressable
              key={r}
              onPress={() => handleSelectRole(r)}
              style={{
                flex: 1,
                backgroundColor: fc.cardBg,
                borderWidth: 1,
                borderColor: fc.border,
                borderRadius: 14,
                paddingVertical: 24,
                paddingHorizontal: 20,
                alignItems: 'center',
                gap: 6,
              }}
            >
              <Text style={{ color, fontSize: 18, fontWeight: '700' }}>{label}</Text>
              <Text style={{ color: fc.textTertiary, fontSize: 13, textAlign: 'center' }}>{sub}</Text>
            </Pressable>
          ))}
        </View>
        <Pressable onPress={() => setStep('check-position')} style={{ marginTop: 16 }}>
          <Text style={{ color: fc.textFaint, fontSize: 13 }}>
            Already on the list? <Text style={{ color: accent, fontWeight: '600' }}>Check your position</Text>
          </Text>
        </Pressable>
      </Animated.View>
    );
  }

  // ── Step 2: Details + Email + submit ──
  const isSubmitDisabled = !email.trim() || !fullName.trim() || !captchaToken || formState === 'submitting';

  const inputStyle = {
    backgroundColor: fc.inputBg,
    borderWidth: 1,
    borderColor: fc.borderInput,
    borderRadius: 10,
    paddingHorizontal: 16,
    paddingVertical: 12,
    color: fc.textPrimary,
    fontSize: 14,
  } as const;

  return (
    <Animated.View entering={FadeIn.duration(400)} style={{ alignItems: 'center', gap: 14, width: '100%' }}>
      <StepDots />
      <Pressable onPress={handleBack} style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
        <ChevronLeft size={16} color={fc.textTertiary} />
        <Text style={{ color: fc.textTertiary, fontSize: 13 }}>Back</Text>
      </Pressable>

      {/* Selection summary */}
      <View style={{ flexDirection: 'row', gap: 8, marginBottom: 2 }}>
        <View style={{ backgroundColor: accentDim, borderRadius: 16, paddingHorizontal: 12, paddingVertical: 4 }}>
          <Text style={{ color: accent, fontSize: 12, fontWeight: '600' }}>
            {role === 'startup' ? 'Startup' : 'Investor'}
          </Text>
        </View>
      </View>

      <View style={{ maxWidth: 480, width: '100%', gap: 10 }}>
        {/* Row 1: Name + Title */}
        <View style={{ flexDirection: 'row', gap: 10 }}>
          <TextInput
            value={fullName}
            onChangeText={setFullName}
            placeholder="Full name *"
            placeholderTextColor={fc.textPlaceholder}
            autoCapitalize="words"
            editable={formState !== 'submitting'}
            style={{ ...inputStyle, flex: 1 }}
          />
          <TextInput
            value={title}
            onChangeText={setTitle}
            placeholder="Title (e.g. CEO)"
            placeholderTextColor={fc.textPlaceholder}
            autoCapitalize="words"
            editable={formState !== 'submitting'}
            style={{ ...inputStyle, flex: 1 }}
          />
        </View>

        {/* Row 2: Company + LinkedIn */}
        <View style={{ flexDirection: 'row', gap: 10 }}>
          <TextInput
            value={companyName}
            onChangeText={setCompanyName}
            placeholder={role === 'investor' ? 'Fund / Group (optional)' : 'Startup name'}
            placeholderTextColor={fc.textPlaceholder}
            autoCapitalize="words"
            editable={formState !== 'submitting'}
            style={{ ...inputStyle, flex: 1 }}
          />
          <TextInput
            value={linkedinUrl}
            onChangeText={setLinkedinUrl}
            placeholder="LinkedIn URL"
            placeholderTextColor={fc.textPlaceholder}
            autoCapitalize="none"
            autoCorrect={false}
            editable={formState !== 'submitting'}
            style={{ ...inputStyle, flex: 1 }}
          />
        </View>

        {/* Row 3: Email + Submit */}
        <View style={{ flexDirection: isMobile ? 'column' : 'row', gap: 10 }}>
          <TextInput
            value={email}
            onChangeText={setEmail}
            placeholder={role === 'investor' ? 'Email *' : 'Work email *'}
            placeholderTextColor={fc.textPlaceholder}
            keyboardType="email-address"
            autoCapitalize="none"
            autoCorrect={false}
            editable={formState !== 'submitting'}
            style={{ ...inputStyle, flex: 1 }}
          />
          <Pressable
            onPress={handleSubmit}
            disabled={isSubmitDisabled}
            style={{
              backgroundColor: isSubmitDisabled
                ? (role === 'investor' ? 'rgba(129,140,248,0.4)' : 'rgba(79,209,197,0.4)')
                : accent,
              borderRadius: 10,
              paddingHorizontal: 24,
              paddingVertical: 12,
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Text style={{
              color: fc.ctaBg,
              fontWeight: '700',
              fontSize: 15,
            }}>
              {formState === 'submitting' ? 'Joining...' : 'Join Waitlist'}
            </Text>
          </Pressable>
        </View>

        {/* Personal email warning */}
        {isPersonalEmail && (
          <Animated.View entering={FadeIn.duration(300)} style={{
            flexDirection: 'row',
            alignItems: 'flex-start',
            gap: 8,
            backgroundColor: 'rgba(250,204,21,0.1)',
            borderRadius: 8,
            padding: 10,
            borderWidth: 1,
            borderColor: 'rgba(250,204,21,0.2)',
          }}>
            <AlertTriangle size={16} color="#facc15" style={{ marginTop: 1 }} />
            <Text style={{ color: '#facc15', fontSize: 12, flex: 1, lineHeight: 17 }}>
              Personal emails may not be considered for beta access. Use your work email for priority access.
            </Text>
          </Animated.View>
        )}
      </View>

      {/* Turnstile CAPTCHA */}
      <TurnstileWidget
        siteKey={TURNSTILE_SITE_KEY}
        onVerify={(token) => setCaptchaToken(token)}
        onExpire={() => setCaptchaToken(null)}
        onError={() => setCaptchaToken(null)}
        theme={fc.captchaTheme}
        size="normal"
      />

      {/* Error message */}
      {formState === 'error' && errorMessage && (
        <Animated.View entering={FadeInUp.duration(300)}>
          <Text style={{ color: '#f87171', fontSize: 14 }}>
            {errorMessage}
          </Text>
        </Animated.View>
      )}
    </Animated.View>
  );
}
